<template>
  <Teleport to="body">
    <!-- Update Available Banner -->
    <Transition name="slide-up">
      <div
        v-if="isUpdateAvailable && !dismissed"
        class="pwa-update-prompt"
        role="alert"
        aria-live="polite"
      >
        <div class="update-content">
          <div class="update-icon">
            <i class="bi bi-arrow-repeat" aria-hidden="true"></i>
          </div>
          <div class="update-text">
            <strong>Update Available</strong>
            <span>A new version of the app is ready.</span>
          </div>
        </div>
        <div class="update-actions">
          <button
            class="btn-dismiss"
            @click="dismissUpdate"
            aria-label="Dismiss update notification"
          >
            Later
          </button>
          <button
            class="btn-update"
            @click="applyUpdate"
            aria-label="Update the application now"
          >
            <i class="bi bi-arrow-clockwise" aria-hidden="true"></i>
            Update Now
          </button>
        </div>
      </div>
    </Transition>

    <!-- Offline Indicator -->
    <Transition name="fade">
      <div
        v-if="!isOnline"
        class="offline-indicator"
        role="status"
        aria-live="polite"
      >
        <i class="bi bi-wifi-off" aria-hidden="true"></i>
        <span>You're offline</span>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref } from 'vue';
import { useServiceWorker } from '@/composables/useServiceWorker';

const {
  isOnline,
  isUpdateAvailable,
  updateServiceWorker
} = useServiceWorker();

const dismissed = ref(false);

async function applyUpdate() {
  await updateServiceWorker(true);
}

function dismissUpdate() {
  dismissed.value = true;
}
</script>

<style scoped>
.pwa-update-prompt {
  position: fixed;
  bottom: 1.5rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.875rem 1.25rem;
  background: linear-gradient(135deg, #212529, #343a40);
  color: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
  z-index: 10001;
  max-width: calc(100vw - 2rem);
}

.update-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.update-icon {
  width: 40px;
  height: 40px;
  background: rgba(123, 143, 212, 0.2);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.update-icon i {
  font-size: 1.25rem;
  color: #7B8FD4;
  animation: spin 2s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.update-text {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.update-text strong {
  font-size: 0.9375rem;
  font-weight: 600;
}

.update-text span {
  font-size: 0.8125rem;
  opacity: 0.8;
}

.update-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: 0.5rem;
}

.btn-dismiss {
  padding: 0.5rem 0.875rem;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.8125rem;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-dismiss:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.3);
}

.btn-update {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.875rem;
  background: linear-gradient(135deg, #7B8FD4, #5a6fb8);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-update:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(123, 143, 212, 0.4);
}

.btn-update i {
  font-size: 0.875rem;
}

/* Offline Indicator */
.offline-indicator {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #ffc107;
  color: #212529;
  font-size: 0.875rem;
  font-weight: 500;
  z-index: 10002;
  text-align: center;
}

.offline-indicator i {
  font-size: 1rem;
}

/* Transitions */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(20px);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .pwa-update-prompt {
    bottom: calc(1rem + env(safe-area-inset-bottom));
    left: 1rem;
    right: 1rem;
    transform: none;
    flex-direction: column;
    gap: 0.75rem;
    padding: 1rem;
  }

  .update-content {
    width: 100%;
  }

  .update-actions {
    width: 100%;
    margin-left: 0;
    justify-content: flex-end;
  }

  .btn-dismiss,
  .btn-update {
    padding: 0.625rem 1rem;
    min-height: 44px;
  }

  .offline-indicator {
    padding: 0.625rem 1rem;
    padding-top: calc(0.625rem + env(safe-area-inset-top));
  }
}

/* Adjust for bottom navigation on mobile */
@media (max-width: 768px) {
  .pwa-update-prompt {
    bottom: calc(4.5rem + env(safe-area-inset-bottom));
  }
}
</style>
